import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "../../utils/cn";

type DSButtonVariant = "link" | "primary" | "secondary" | "tertiary";
type DSButtonType = "default" | "icon";
type DSButtonSize = "default" | "prominent";

type DSButtonProps = {
  buttonType?: DSButtonType;
  children: ReactNode;
  className?: string;
  size?: DSButtonSize;
  variant?: DSButtonVariant;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "className">;

const variantClassByName: Record<DSButtonVariant, string> = {
  link:
    "bg-transparent text-brand-primary-3 hover:opacity-90 disabled:opacity-50 disabled:text-gray-4 aria-disabled:opacity-50 aria-disabled:text-gray-4",
  primary:
    "bg-brand-primary-3 text-body-light-mode-1 hover:opacity-90 disabled:opacity-50 aria-disabled:opacity-50",
  secondary:
    "bg-dark-light-mode-background hover:bg-gray-4 text-dark-3 disabled:bg-gray-3 disabled:text-gray-4 aria-disabled:bg-gray-3 aria-disabled:text-gray-4 aria-disabled:hover:bg-dark-light-mode-background",
  tertiary:
    "bg-transparent text-dark-3 hover:bg-gray-2 disabled:bg-gray-3 disabled:text-gray-4 aria-disabled:bg-gray-3 aria-disabled:text-gray-4 aria-disabled:hover:bg-transparent",
};

const typeClassByName: Record<DSButtonType, string> = {
  default: "h-10 w-full",
  icon: "size-10 p-0",
};

const sizeClassByName: Record<DSButtonSize, string> = {
  default: "px-4 py-2 text-[14px]",
  prominent: "px-8 py-2 text-base",
};

function DSButton({
  buttonType = "default",
  children,
  className,
  size = "default",
  type = "button",
  variant = "primary",
  ...props
}: DSButtonProps) {
  return (
    <button
      className={cn(
        "inline-flex cursor-pointer aria-disabled:cursor-not-allowed shrink-0 items-center justify-center gap-2 rounded-[12px] font-inter font-semibold leading-[normal] tracking-[0px] transition-[background-color,border-color,color,opacity,transform] duration-150 ease-out disabled:cursor-not-allowed disabled:pointer-events-none",
        typeClassByName[buttonType],
        buttonType === "default" ? sizeClassByName[size] : undefined,
        variantClassByName[variant],
        className
      )}
      type={type}
      {...props}
    >
      {children}
    </button>
  );
}

export { DSButton };
export type { DSButtonProps, DSButtonSize, DSButtonType, DSButtonVariant };
